import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Settings, Globe, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';

const WorkspaceSettings = () => {
  const { toast } = useToast();
  const { language, setLanguage } = useLanguage();
  const [workspaceName, setWorkspaceName] = useState('My Workspace');
  const [timezone, setTimezone] = useState('UTC');
  const [loading, setLoading] = useState(false);

  const handleLanguageChange = (newLanguage: string) => {
    setLanguage(newLanguage as 'en' | 'pt' | 'es');
    toast({
      title: "Language updated",
      description: "Your workspace language has been changed.",
    });
  };

  const handleSave = async () => {
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Workspace</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Manage workspace-wide settings and preferences
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5" />
            General Settings
          </CardTitle>
          <CardDescription>
            Configure basic workspace settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="workspace-name">Workspace Name</Label>
            <Input
              id="workspace-name"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              placeholder="Enter workspace name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="language">Language</Label>
            <Select value={language} onValueChange={handleLanguageChange}>
              <SelectTrigger id="language">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="es">Español</SelectItem>
                <SelectItem value="pt">Português</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="timezone">Timezone</Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger id="timezone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UTC">UTC (GMT +0:00)</SelectItem>
                <SelectItem value="America/New_York">Eastern Time (GMT -5:00)</SelectItem>
                <SelectItem value="America/Los_Angeles">Pacific Time (GMT -8:00)</SelectItem>
                <SelectItem value="Europe/London">London (GMT +0:00)</SelectItem>
                <SelectItem value="Europe/Paris">Paris (GMT +1:00)</SelectItem>
                <SelectItem value="Asia/Tokyo">Tokyo (GMT +9:00)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end pt-4">
            <Button onClick={handleSave} disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Data & Privacy
          </CardTitle>
          <CardDescription>
            Control your data and privacy settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium text-sm">Data Export</h4>
            <p className="text-sm text-muted-foreground">
              Download all your workspace data in a portable format
            </p>
            <Button variant="outline" size="sm">
              Export Data
            </Button>
          </div>

          <div className="space-y-2">
            <h4 className="font-medium text-sm text-destructive">Delete Workspace</h4>
            <p className="text-sm text-muted-foreground">
              Permanently delete this workspace and all associated data
            </p>
            <Button variant="destructive" size="sm">
              Delete Workspace
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkspaceSettings;